import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Button, Card } from './ui.js';

/**
 * The last line of defense in the browser.
 *
 * Without one, a render error leaves a blank white page — and a Class 10
 * student on a borrowed phone has no console to look at and no way to tell a
 * broken app from a slow one. Reloading is the only useful action, so it is
 * the only one offered.
 *
 * One failure is not a bug at all: every page but sign-in is loaded on demand,
 * and a page opened while the connection is down fails to download. That one
 * says to check the connection and reloads by itself when the browser is back
 * online. A reload rather than a reset, because `React.lazy` keeps the failed
 * import and would throw it again.
 */
interface State {
  error: Error | null;
}

/**
 * A dynamic `import()` that did not arrive. Each browser words it its own way:
 * Chrome "Failed to fetch dynamically imported module", Firefox "error loading
 * dynamically imported module", Safari "Importing a module script failed", and
 * Vite's preload helper "Unable to preload CSS".
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  if (error.name === 'ChunkLoadError') return true;
  return /dynamically imported module|importing a module script failed|unable to preload css|loading (css )?chunk/i.test(
    error.message,
  );
}

interface Props {
  children: ReactNode;
  /**
   * When this changes, a caught error is cleared and the children render
   * again — the path, so leaving a broken page leaves its error behind. A
   * reset rather than a remount, which would refetch every page on a tab switch.
   */
  resetKey?: string;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    if (isChunkLoadError(error)) {
      window.addEventListener('online', reload);
      return;
    }
    // Goes to the browser console for a developer, and is the hook where an
    // error tracker is wired in for the pilot (section 10).
    console.error('Unhandled error in the app', error, info.componentStack);
  }

  override componentDidUpdate(previous: Props): void {
    if (this.state.error && previous.resetKey !== this.props.resetKey) {
      window.removeEventListener('online', reload);
      this.setState({ error: null });
    }
  }

  override componentWillUnmount(): void {
    window.removeEventListener('online', reload);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (isChunkLoadError(error)) {
      return (
        <div className="mx-auto max-w-md px-4 py-16">
          <Card className="p-6 text-center">
            <h1 className="text-xl font-semibold">This page could not load</h1>
            <p className="mt-2 text-sm text-ink-soft">
              Check your connection. Your work is saved, and the page loads by itself when you are
              back online.
            </p>
            <Button variant="primary" className="mt-5 w-full" onClick={reload}>
              Try again
            </Button>
          </Card>
        </div>
      );
    }

    return (
      <div className="mx-auto max-w-md px-4 py-16">
        <Card className="p-6 text-center">
          <h1 className="text-xl font-semibold">Something went wrong</h1>
          <p className="mt-2 text-sm text-ink-soft">
            Your work is saved. Reloading usually fixes this. If it keeps happening, tell your
            teacher.
          </p>
          <Button variant="primary" className="mt-5 w-full" onClick={reload}>
            Reload
          </Button>
        </Card>
      </div>
    );
  }
}

function reload(): void {
  window.location.reload();
}
